import Loading from "@/components/loading/loading.vue";
import { ContentTitleModule } from "@/modules/content-title";
import { ConversationBuilderState } from "@/modules/conversation-builder";
import UnitState from "@/modules/unit";
import { VersionGroupModule } from "@/modules/versiongroup";
import { ContainerType } from "@/services/admin";
import { ContentCMImage, ContentService, ContentTitleModel } from "@/services/content";
import { VCSLevelUnitResponseModel } from "@/services/text-guardian/unit-list/model";
import { EmptyGuid } from "@/util/constants";
import { BuilderPageType, ContentCategoryEnum } from "@/util/enum";
import { Form, Input, Select } from "ant-design-vue";
import { WrappedFormUtils } from "ant-design-vue/types/form/form";
import { debounce, isUndefined, last, merge } from "lodash";
import { Component, Prop, Vue } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

const UNIT_ALL = 0;
const CONTENT_ALL = "All";

@Component({
	components: {
		Search: Input.Search,
		Select,
		Option: Select.Option,
		Form,
		FormItem: Form.Item,
		Loading
	}
})
export default class ConversationImageContent extends Vue {
	@Prop({ default: "" }) contentId!: string;
	@Prop({ default: "" }) pageId!: string;
	public form!: WrappedFormUtils;
	public loading: boolean = false;
	public images: ContentCMImage[] = [];
	public versionGroups = VersionGroupModule.versionGroups;
	private unitModule = getModule(UnitState);
	public units: VCSLevelUnitResponseModel[] = [];
	public titles: ContentTitleModel[] = [];
	public titlesLoading: boolean = false;
	public showVersionGroup: boolean = false;

	private conversationStore = getModule(ConversationBuilderState);
	private signatureBase!: string;
	private signatureParams!: string;
	private firstLoad = true;

	private formEmitEvent = true;

	created() {
		const debouncedValueChange = debounce(this.onValuesChange, 500);
		const onValuesChange = (props, fields) => {
			if (this.formEmitEvent) {
				if (fields.versionGroupId) {
					this.setUnits(fields);
				}
				if (!isUndefined(fields.versionGroupId) || !isUndefined(fields.unitId)) {
					this.setTitles(fields);
				}
			}
			this.formEmitEvent = true;

			return debouncedValueChange();
		};
		this.form = this.$form.createForm(this, {
			name: "image-editor-form",
			onValuesChange: onValuesChange.bind(this)
		});
	}
	async mounted() {
		let defaultVersionGroupId = this.conversationStore.conversationExtraInfo?.versionGroupId;

		if (!defaultVersionGroupId || defaultVersionGroupId === EmptyGuid) {
			defaultVersionGroupId = this.versionGroups.find((versionGroup) => versionGroup.isPublished)?.id;
			this.showVersionGroup = true;
		}

		const promises: Promise<any>[] = [this.conversationStore.getDownloadSasURL()];

		promises.push(
			ContentTitleModule.getTitles({
				versionGroupId: defaultVersionGroupId as string
			})
		);

		const [_, allTitles] = await Promise.all(promises);
		this.setSasUrl();
		const isContentPresent = allTitles.find((f) => f.contentId === this.contentId);
		let selectedContentId = isContentPresent
			? this.contentId
			: false || this.$route.params.contentId || this.$route.params.id || CONTENT_ALL;
		if (this.$route.params.pagetype === BuilderPageType.MasterInfo) {
			selectedContentId = CONTENT_ALL;
		}
		const unitId = isContentPresent ? allTitles.find((t) => t.contentId === this.contentId)?.unitId : null;
		this.form.setFieldsValue({
			unitId: unitId || this.conversationStore.conversationExtraInfo?.unitId || UNIT_ALL,
			versionGroupId: defaultVersionGroupId,
			keyword: null,
			contentId: selectedContentId
		});
	}

	private setSasUrl() {
		const pageSignature = this.conversationStore.downloadSASURL?.find(
			(signature) => signature.type === ContainerType.Page
		);
		if (pageSignature) {
			const splitedUrl = pageSignature.url.split("?");
			this.signatureBase = splitedUrl[0] + pageSignature.name + "/";
			this.signatureParams = "?" + splitedUrl[1];
		}
	}

	public getImageUrl(path: string) {
		return this.signatureBase + path + this.signatureParams;
	}

	public onValuesChange() {
		this.form.validateFields((err, values) => {
			const { versionGroupId, keyword, unitId, contentId } = values;
			const hasContentId = !!contentId && contentId !== CONTENT_ALL;
			if (err || (!hasContentId && !keyword)) {
				return null;
			}
			this.loading = true;
			this.images = [];
			ContentService.getImages(
				versionGroupId,
				keyword,
				unitId,
				contentId === CONTENT_ALL ? null : contentId,
				ContentCategoryEnum.CM
			)
				.then((images) => {
					this.images = images;
					if (this.firstLoad) {
						this.firstLoad = false;

						const image = images.find((i) => i.imageDetail.pageId === this.pageId);
						if (image) {
							this.edit(image, true);
						}
					}
					this.loading = false;
				})
				.catch(() => {
					this.loading = false;
				});
		});
	}

	private setUnits(fields: any) {
		const { versionGroupId } = fields;
		if (versionGroupId) {
			const versionGroup = this.versionGroups.find((v) => v.id === versionGroupId);
			this.units = this.unitModule.units.filter((unit) => unit.curriculumType === versionGroup?.curriculumType);
		} else {
			this.units = this.unitModule.units;
		}
		this.setDefaultUnitId();
	}
	private setTitles(fields: any) {
		const { versionGroupId, unitId } = merge(this.form.getFieldsValue(), fields);
		this.titlesLoading = true;
		ContentTitleModule.getTitles({
			versionGroupId,
			unitId
		}).then(
			(titles) => {
				this.titlesLoading = false;
				if (unitId === UNIT_ALL) {
					this.titles = [];
				} else {
					this.titles = titles;
				}
				this.setDefaultContentId();
			},
			() => {
				this.titlesLoading = false;
			}
		);
	}

	private setDefaultContentId(): void {
		const contentId = this.form.getFieldValue("contentId");
		if (contentId) {
			const hasContentTitle = this.titles.find((title) => title.contentId === contentId);
			if (!hasContentTitle) {
				this.formEmitEvent = false;
				this.form.setFieldsValue({ contentId: CONTENT_ALL });
			}
		}
	}
	private setDefaultUnitId(): void {
		const unitId = this.form.getFieldValue("unitId");
		if (unitId) {
			const hasUnitId = this.units.find((unit) => unit.id === unitId);
			if (!hasUnitId) {
				this.formEmitEvent = false;
				this.form.setFieldsValue({ unitId: 0 });
			}
		}
	}

	public edit(item: ContentCMImage, applyMetaData: boolean = false) {
		const fileName = last(item.imageUrl.split("/"));
		this.$emit(
			"edit",
			this.getImageUrl(item.imageUrl),
			fileName,
			"image/png",
			item.imageDetail,
			applyMetaData,
			item.contentId
		);
	}

	public titleSearch(value: string) {
		const { unitId, versionGroupId } = this.form.getFieldsValue();
		if (!value?.length && unitId !== UNIT_ALL) {
			ContentTitleModule.getTitles({
				versionGroupId,
				unitId
			}).then((titles) => {
				this.titles = titles;
			});
			return;
		}
		if (value?.length < 2) {
			return;
		}

		ContentTitleModule.getTitles({
			versionGroupId
		}).then((titles) => {
			this.titles = titles.filter((title) => {
				if (unitId && title.unitId !== unitId) {
					return false;
				}
				return title.title.toLowerCase().indexOf(value) >= 0;
			});
		});
	}
}
